import React, { useState, useEffect, useRef } from "react";

interface Option {
    value: string;
    label: string;
}

interface Props {
    selectOptions: Option[];
    admissionNo: string;
    setAdmissionNo: (value: string) => void;
}

const StudentAdmissionDropdown: React.FC<Props> = ({ selectOptions, admissionNo, setAdmissionNo }) => {
    const [filteredOptions, setFilteredOptions] = useState<Option[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Filter options on input change
    const handleInputChange = (value: string) => {
        setAdmissionNo(value);
        if (value.trim() === "") {
            setFilteredOptions([]);
            setShowDropdown(false);
            setHighlightedIndex(-1);
        } else {
            const filtered = selectOptions.filter(option =>
                option.label.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredOptions(filtered);
            setShowDropdown(true);
            setHighlightedIndex(0);
        }
    };

    // Keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showDropdown) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex(prev => (prev + 1) % filteredOptions.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex(prev => (prev - 1 + filteredOptions.length) % filteredOptions.length);
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                setAdmissionNo(filteredOptions[highlightedIndex].value);
                setShowDropdown(false);
            }
        } else if (e.key === "Escape") {
            setShowDropdown(false);
        }
    };

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={dropdownRef} style={{ width: "300px", position: "relative" }}>
            <input
                ref={inputRef}
                type="text"
                value={admissionNo}
                onChange={e => handleInputChange(e.target.value)}
                onFocus={() => admissionNo && setShowDropdown(true)}
                onKeyDown={handleKeyDown}
                placeholder="Select Admission Number"
                className="form-control"
                autoComplete="off"
            />
            {admissionNo && (
                <button
                    onClick={() => {
                        setAdmissionNo("");
                        setFilteredOptions([]);
                        setShowDropdown(false);
                    }}
                    style={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        padding: "0 5px",
                    }}
                >
                    ✕
                </button>
            )}
            {showDropdown && filteredOptions.length > 0 && (
                <ul
                    style={{
                        listStyle: "none",
                        margin: 0,
                        padding: 0,
                        border: "1px solid #ccc",
                        borderTop: "none",
                        maxHeight: "150px",
                        overflowY: "auto",
                        position: "absolute",
                        width: "100%",
                        background: "#fff",
                        zIndex: 10,
                    }}
                >
                    {filteredOptions.map((option, index) => (
                        <li
                            key={option.value}
                            onClick={() => {
                                setAdmissionNo(option.value);
                                setShowDropdown(false);
                            }}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            style={{
                                padding: "5px 10px",
                                cursor: "pointer",
                                background: index === highlightedIndex ? "#10296C" : "#fff",
                                color: index === highlightedIndex ? "#fff" : "#000",
                            }}
                        >
                            {option.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default StudentAdmissionDropdown;
